/**
 * AirportAutocomplete — searchable airport picker backed by the mock Airports API.
 *
 * Shows Airport Name, Code, and "City, Country" for each result. Debounces queries,
 * supports keyboard navigation (↑/↓/Enter/Esc), and closes on outside click.
 *
 * @param {Object} props
 * @param {string} props.label Field label.
 * @param {import('../../mock/types').Airport | null} props.value Selected airport.
 * @param {(airport: import('../../mock/types').Airport) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string} [props.icon] FontAwesome class for the leading icon.
 * @param {string} [props.excludeCode] Airport code to hide (e.g. the other field's value).
 */
import React, { useEffect, useRef, useState } from "react";
import { getAirports } from "../../mock/api";
import "./airport-autocomplete.css";

export default function AirportAutocomplete({
  label,
  value,
  onChange,
  placeholder = "City or airport",
  icon = "fas fa-plane-departure",
  excludeCode,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [activeTab, setActiveTab] = useState("all"); // "all" | "domestic" | "international"
  const wrapRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced fetch when the query changes and the dropdown is open.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    const t = setTimeout(() => {
      getAirports(query)
        .then((list) => {
          if (!active) return;
          setResults(list.filter((a) => a.airportCode !== excludeCode));
          setHighlight(0);
        })
        .catch(() => active && setError(true))
        .finally(() => active && setLoading(false));
    }, 220);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, open, excludeCode]);

  const select = (airport) => {
    onChange(airport);
    setQuery("");
    setOpen(false);
  };

  const filteredResults = React.useMemo(() => {
    if (activeTab === "all") return results.slice(0, 25);
    return results.filter((a) => a.type === activeTab).slice(0, 25);
  }, [results, activeTab]);

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filteredResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredResults[highlight]) select(filteredResults[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const displayValue = open
    ? query
    : value
    ? `${value.city} (${value.airportCode})`
    : "";

  return (
    <div className="aac" ref={wrapRef}>
      {label && <label className="aac__label">{label}</label>}
      <div className={`aac__control ${open ? "aac__control--open" : ""}`}>
        <i className={`${icon} aac__icon`} aria-hidden="true" />
        <input
          type="text"
          className="aac__input"
          value={displayValue}
          placeholder={value && !open ? "" : placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          aria-label={label}
        />
        {value && !open && (
          <span className="aac__selected-code">{value.airportCode}</span>
        )}
      </div>

      {open && (
        <div className="aac__dropdown" role="listbox">
          <div className="aac__tabs">
            <button
              type="button"
              className={`aac__tab ${activeTab === "all" ? "aac__tab--active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("all");
                setHighlight(0);
              }}
            >
              All
            </button>
            <button
              type="button"
              className={`aac__tab ${activeTab === "domestic" ? "aac__tab--active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("domestic");
                setHighlight(0);
              }}
            >
              Domestic
            </button>
            <button
              type="button"
              className={`aac__tab ${activeTab === "international" ? "aac__tab--active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("international");
                setHighlight(0);
              }}
            >
              International
            </button>
          </div>

          {loading && <div className="aac__msg">Searching…</div>}
          {error && !loading && (
            <div className="aac__msg aac__msg--error">Couldn't load airports.</div>
          )}
          {!loading && !error && filteredResults.length === 0 && (
            <div className="aac__msg">No airports found.</div>
          )}
          {!loading &&
            !error &&
            filteredResults.map((a, i) => (
              <button
                type="button"
                key={a.id}
                role="option"
                aria-selected={i === highlight}
                className={`aac__option ${i === highlight ? "aac__option--active" : ""}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => select(a)}
              >
                <span className="aac__opt-icon">
                  <i className="fas fa-plane" aria-hidden="true" />
                </span>
                <span className="aac__opt-body">
                  <span className="aac__opt-name">{a.airportName}</span>
                  <span className="aac__opt-sub">
                    {a.city}, {a.country} <span className="aac__badge-type">{a.type}</span>
                  </span>
                </span>
                <span className="aac__opt-code">{a.airportCode}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
